// 上の帯の兵糧を押すと開く小窓：本体と予備・次の回復・今日あと回復する量・満タンまで・上限の内訳・出撃できる回数。
// 計算はサーバー（core の meta/stamina.ts）と同じ決まり：4分に1回復、本体が上限なら予備へ。月パス中は3倍速で、1日（JST）の自然回復は dailyRegen まで。
// 画面の兵糧（state.stamina）を手元で進めて出す。次にサーバーから受け取れば揃う。
import { META_CONFIG, PRODUCT_BY_ID, levelTier, monthlyActive, stageStaminaCost } from '@hyakki/core';
import { api } from '../api';
import { go } from '../router';
import { state, levelNow } from '../state';
import { advanceStamina } from '../stamina-tick';
import { esc, fmt, hhmm, resIcon } from './dom';
import { openModal } from './overlay';

const JST = 9 * 3_600_000;
const DAY = 86_400_000;
const jstDay = (t: number) => new Date(t + JST).toISOString().slice(0, 10);
const nextMidnight = (t: number) => Math.floor((t + JST) / DAY) * DAY + DAY - JST;

/** 「あと 2時間15分」 */
function left(ms: number) {
  if (ms <= 0) return 'まもなく';
  const m = Math.ceil(ms / 60_000);
  if (m < 60) return `あと${m}分`;
  const h = Math.floor(m / 60);
  return h >= 48 ? `あと${Math.floor(h / 24)}日${h % 24}時間` : `あと${h}時間${m % 60 ? `${m % 60}分` : ''}`;
}
const at = (t: number) => (jstDay(t) === jstDay(Date.now()) ? hhmm(t) : `${new Date(t).getMonth() + 1}/${new Date(t).getDate()} ${hhmm(t)}`);

/** 今の兵糧から、回復を1つずつ進めて「今日あと何回復するか」「本体・予備が満タンになる時刻」を出す（月パスの1日の上限も数える） */
function forecast(now: number) {
  const st0 = state.stamina!;
  const p = state.profile;
  const base = META_CONFIG.stamina.regenMinutes * 60_000;
  const st = advanceStamina(st0, now, base);
  const fast = !!p && monthlyActive(p, now);
  const per = fast ? base / META_CONFIG.monthly.regenSpeed : base;
  const cap = META_CONFIG.monthly.dailyRegen;
  const today = jstDay(now);
  // 月パスの今日の回復の数（プロフィールの兵糧の記録。出撃のあとは少し古いことがある）
  const rec = p?.stamina;
  const gotToday = fast && rec?.day === today ? (rec.got ?? 0) : 0;
  const midnight = nextMidnight(now);
  let { value, reserve } = st;
  let t = st.nextAt;
  let day = today;
  let got = gotToday;
  let todayLeft = 0;
  let mainFull: number | null = value >= st.max ? now : null;
  let reserveFull: number | null = reserve >= st.reserveMax ? now : null;
  // 予備まで満タン、またはおよそ10日先で打ち切る
  for (let i = 0; t != null && i < 20_000 && (value < st.max || reserve < st.reserveMax) && t < now + 10 * DAY; i++) {
    if (fast && p?.monthlyUntil && t > p.monthlyUntil) break; // 月パスが切れた先は概算しない
    if (fast) {
      const d = jstDay(t);
      if (d !== day) {
        day = d;
        got = 0;
      }
      if (got >= cap) {
        t = nextMidnight(t) + per;
        continue;
      }
      got++;
    }
    if (value < st.max) value++;
    else reserve++;
    if (t < midnight) todayLeft++;
    if (mainFull == null && value >= st.max) mainFull = t;
    if (reserveFull == null && reserve >= st.reserveMax) reserveFull = t;
    t += per;
  }
  return { st, fast, per, cap, gotToday, todayLeft, mainFull, reserveFull };
}

/** 蔵出しで買った兵糧：合計と、いちばん近い期限（チャットチケットの所持と同じ出し方） */
function kuraRow(now: number, row: (k: string, v: string) => string) {
  const lots = (state.stamina?.boughtLots ?? []).filter((l) => l.until > now);
  if (!lots.length) return '';
  const n = lots.reduce((a, l) => a + l.n, 0);
  const d = new Date(lots[0].until);
  const until = `${d.getMonth() + 1}/${d.getDate()}まで`;
  return row('うち蔵出し', `${fmt(n)}<small>（${lots[0].n === n ? until : `うち${fmt(lots[0].n)}は${until}`}）</small>`);
}

function html(now: number) {
  const p = state.profile;
  const f = forecast(now);
  const { st } = f;
  const S = META_CONFIG.stamina;
  const kura = p?.village?.levels.hyorogura ?? 0;
  const paid = (p?.entitlements ?? []).includes('reserve_plus');
  const cost = stageStaminaCost(1);
  const meter = (v: number, m: number) => `<div class="meter"><i style="width:${m ? Math.min(100, (v / m) * 100) : 0}%"></i></div>`;
  const row = (k: string, v: string) => `<div><dt>${esc(k)}</dt><dd>${v}</dd></div>`;
  const full = (t: number | null, v: number, m: number) => (v >= m ? '満タン' : t == null ? '—' : `${left(t - now)}（${at(t)}）`);
  return `<div class="sti">
    <div class="sti-now">
      <div class="sti-part"><span class="sti-k">本体</span><b>${fmt(st.value)}<small>/${fmt(st.max)}</small></b>${meter(st.value, st.max)}</div>
      <div class="sti-part"><span class="sti-k">予備</span><b>${fmt(st.reserve)}<small>/${fmt(st.reserveMax)}</small></b>${meter(st.reserve, st.reserveMax)}</div>
    </div>
    <dl class="sti-kv">
      ${kuraRow(now, row)}
      ${row('出撃できる回数', `${fmt(Math.floor((st.value + st.reserve) / cost))}回<small>（1戦 ${cost}）</small>`)}
      ${row('回復の速さ', f.fast ? `${Math.round(f.per / 1000)}秒に1<small>（月パス 3倍速）</small>` : `${S.regenMinutes}分に1`)}
      ${row('次の回復', st.nextAt ? left(st.nextAt - now) : '満タン')}
      ${row('今日あと回復する量', `${fmt(f.todayLeft)}<small>（0時まで）</small>`)}
      ${f.fast ? row('今日の回復', `${fmt(f.gotToday)}<small>/${fmt(f.cap)}</small>`) : ''}
      ${row('本体が満タンまで', full(f.mainFull, st.value, st.max))}
      ${row('予備が満タンまで', full(f.reserveFull, st.reserve, st.reserveMax))}
    </dl>
    <h3 class="sti-h">上限の内訳</h3>
    <dl class="sti-kv sti-break">
      ${row('本体', `${fmt(S.baseMax)}<small>＋主Lv ${fmt(levelTier(levelNow()))}＋兵糧庫 ${fmt(kura * META_CONFIG.village.hyorograMaxPerLevel)}</small>＝${fmt(st.max)}`)}
      ${row('予備', `${fmt(S.reserveMax)}<small>＋兵糧庫 ${fmt(kura * META_CONFIG.village.hyorograReservePerLevel)}${paid ? `＋予備枠 ${fmt(S.paidReserveBonus)}` : ''}</small>＝${fmt(st.reserveMax)}`)}
    </dl>
  </div>`;
}

/** 兵糧が足りないときの小窓の中身：いまの兵糧と1戦の量・出撃できるまで、蔵出し（今日まだ買えるときだけ）と月パス（月パス中は出さない） */
function shortHtml(need: number, now: number, kuraOk: boolean) {
  const base = META_CONFIG.stamina.regenMinutes * 60_000;
  const st = advanceStamina(state.stamina!, now, base);
  const fast = !!state.profile && monthlyActive(state.profile, now);
  const per = fast ? base / META_CONFIG.monthly.regenSpeed : base;
  const have = st.value + st.reserve;
  const wait = have >= need ? '出撃できます' : st.nextAt ? left(st.nextAt - now + (need - have - 1) * per) : '—';
  const item = (id: string, art: string, sub: string) => {
    const p = PRODUCT_BY_ID[id];
    return p ? `<button class="hys-item" data-buy="${id}">${art}<b>${esc(p.name.replace(/（.*）$/, ''))}</b><small>${sub}</small><em>¥${fmt(p.yen)}</em></button>` : '';
  };
  return `<div class="sti hys">
    <div class="sti-now">
      <div class="sti-part"><span class="sti-k">今の兵糧<small>（1戦あたり${fmt(need)}消費）</small></span><b>${fmt(have)}</b></div>
      <div class="sti-part"><span class="sti-k">出撃できるまで</span><b class="hys-wait">${wait}</b></div>
    </div>
    ${
      kuraOk || !fast
        ? `<div class="hys-buy">
      ${kuraOk ? item('kuradashi', resIcon('hyoro'), `兵糧${META_CONFIG.kuradashi.hyoro}`) : ''}
      ${fast ? '' : item('monthly', '<img src="/art/player-avatars/15-kyubi.webp" alt="">', `回復速度${META_CONFIG.monthly.regenSpeed}倍<br>有効期限：${META_CONFIG.monthly.days}日`)}
    </div>`
        : ''
    }
  </div>`;
}

/** 兵糧が足りなくて出撃できないとき（出撃ボタン・オートの開始・戦闘の開始）：待つ時間と、蔵出し・月パスへの入口（入口は hyoro_out） */
export function openHyoroShort(need: number) {
  if (!state.stamina) return;
  // 蔵出しは今日まだ買えるか（天下の石高・自分の本日残り）をショップの一覧で確かめてから出す
  let kuraOk = false;
  const draw = () => (m.body.innerHTML = shortHtml(need, Date.now(), kuraOk));
  const m = openModal(shortHtml(need, Date.now(), kuraOk), { title: '兵糧が足りません', className: 'stamina-info-wrap', onClose: () => clearInterval(timer) });
  m.el.querySelector('.modal-head h2')?.insertAdjacentHTML('afterbegin', resIcon('hyoro'));
  const timer = setInterval(() => {
    if (!m.el.isConnected || !state.stamina) return clearInterval(timer);
    draw();
  }, 1000);
  void api
    .products()
    .then((r) => {
      const k = r.products.find((p) => p.id === 'kuradashi');
      kuraOk = r.enabled && !!k && !!k.kura?.koku && k.remainingToday !== 0;
      if (m.el.isConnected) draw();
    })
    .catch(() => {});
  m.body.addEventListener('click', (e) => {
    const id = (e.target as HTMLElement).closest<HTMLElement>('[data-buy]')?.dataset.buy;
    if (!id) return;
    m.close();
    go('shop', { cat: id === 'kuradashi' ? 'item' : 'perm', focus: id, from: 'hyoro_out' });
  });
}

/** 兵糧の小窓（開いているあいだ1秒ごとに数え直す） */
export function openStaminaInfo() {
  if (!state.stamina) return;
  const m = openModal(html(Date.now()), { title: `兵糧`, className: 'stamina-info-wrap', onClose: () => clearInterval(timer) });
  m.el.querySelector('.modal-head h2')?.insertAdjacentHTML('afterbegin', resIcon('hyoro'));
  const timer = setInterval(() => {
    if (!m.el.isConnected || !state.stamina) return clearInterval(timer);
    m.body.innerHTML = html(Date.now());
  }, 1000);
}
